import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'react-toastify';
import {
  getOffboarding,
  invalidateOffboardings,
  offboardingKey,
} from '../../services/offboardings';
import { Offboarding } from '../../types/offboardings';

export const OFFBOARDING_POLL_INTERVAL_MS = 3000;
export const OFFBOARDING_POLL_MAX_ATTEMPTS = 40;

type Options = {
  enabled?: boolean;
  poll?: boolean;
  businessId?: string;
  includeSourceCampaign?: boolean;
  onCompleted?: (offboarding: Offboarding) => void;
};

function useGetOffboarding(
  offboardingId: string | null | undefined,
  options: Options = {},
) {
  const {
    enabled = true,
    poll = false,
    businessId,
    includeSourceCampaign = false,
    onCompleted,
  } = options;

  const attemptsRef = React.useRef(0);
  const [pollExhausted, setPollExhausted] = React.useState(false);
  const completedRef = React.useRef(false);

  React.useEffect(() => {
    attemptsRef.current = 0;
    completedRef.current = false;
    setPollExhausted(false);
  }, [offboardingId]);

  const query = useQuery({
    queryKey: [
      ...offboardingKey(offboardingId ?? ''),
      { includeSourceCampaign },
    ],
    queryFn: () =>
      getOffboarding(offboardingId as string, { includeSourceCampaign }),
    enabled: enabled && !!offboardingId,
    refetchInterval: queryArg => {
      if (!poll) return false;
      const { data } = queryArg.state;
      if (!data) return OFFBOARDING_POLL_INTERVAL_MS;
      if (data.completedAt || data.canceledAt) return false;
      const scheduledFor = data.scheduledFor
        ? new Date(data.scheduledFor).getTime()
        : Date.now();
      if (scheduledFor > Date.now()) return false;
      attemptsRef.current += 1;
      if (attemptsRef.current >= OFFBOARDING_POLL_MAX_ATTEMPTS) {
        return false;
      }
      return OFFBOARDING_POLL_INTERVAL_MS;
    },
  });

  const { data } = query;

  React.useEffect(() => {
    if (!poll || !data) return;
    if (data.completedAt && !completedRef.current) {
      completedRef.current = true;
      if (businessId) invalidateOffboardings(businessId);
      onCompleted?.(data);
      return;
    }
    if (
      attemptsRef.current >= OFFBOARDING_POLL_MAX_ATTEMPTS &&
      !data.completedAt &&
      !data.canceledAt &&
      !pollExhausted
    ) {
      setPollExhausted(true);
      toast.error(
        'Offboarding is taking longer than expected. Refresh the page in a minute to see the latest status.',
      );
    }
  }, [businessId, data, onCompleted, poll, pollExhausted]);

  return {
    data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    pollExhausted,
  };
}

export default useGetOffboarding;
